import { Card, Modal, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { PORT_WHARF_BERTH_TYPE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Button
} from "zhihao-ui-react";
import { loadPortManagementDetail } from "./PortManagementFormPage";
import { BerthLocationModal } from "./BerthLocationModal";
import { useUserStore } from "@/stores/react/authStore";

export interface PortManagementDetailPageProps {
    portId?: number | string;
    loadPort?: (id: number) => Promise<PortWharfBerthVo>;
    loadPortCount?: (id: number, type: number) => Promise<{ dockNum: number; berthNum: number }>;
    deletePort?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 港口下属码头/泊位数量接口。 */
export async function loadPortManagementCount(id: number, type: number) {
    const response = await shippingRequestHttp.service.get<{ dockNum: number; berthNum: number }>(
        "/port/find-dock-berth-num",
        { params: { id, type } }
    );
    return response.data;
}

/** 复用 Vue 港口删除接口。 */
export async function deletePortManagement(id: number, type: number) {
    const response = await shippingRequestHttp.service.delete(`/port/${id}`, { params: { id, type } });
    return response.data;
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : value;

/** 物流管理-港口详情，覆盖 Vue 字段展示、编辑入口和删除前数量确认。 */
export function PortManagementDetailPage({
    portId,
    loadPort = loadPortManagementDetail,
    loadPortCount = loadPortManagementCount,
    deletePort = deletePortManagement,
    permissions
}: PortManagementDetailPageProps) {
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const currentId = portId ?? params.id ?? searchParams.get("id") ?? undefined;
    const [detail, setDetail] = useState<PortWharfBerthVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmMessage, setConfirmMessage] = useState("");
    const [deleting, setDeleting] = useState(false);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue v-auth：港口详情的修改、删除入口分别受 DEAL/W 权限控制。
    const currentPermissions = permissions ?? storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PORT_DEAL");
    const canDelete = currentPermissions.includes("ROLE_PORT_W");

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadPort(Number(currentId))
            .then(value => {
                if (!disposed) {
                    setDetail(value);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("港口详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadPort]);

    // 沿用 Vue 删除前的数量分支，防止误删包含码头或泊位的港口。
    const prepareDelete = async () => {
        if (!detail?.id) return;
        try {
            const count = await loadPortCount(Number(detail.id), PORT_WHARF_BERTH_TYPE.HARBOR.code);
            if (count.dockNum === 0 && count.berthNum === 0) setConfirmMessage("是否删除港口信息?");
            else if (count.dockNum > 0 && count.berthNum === 0)
                setConfirmMessage(`港口下有${count.dockNum}个码头，删除港口后将同时删除所有码头。`);
            else
                setConfirmMessage(
                    `港口下有${count.dockNum}个码头、${count.berthNum}个泊位，删除港口后将同时删除所有码头、泊位。`
                );
        } catch {
            setErrorMessage("港口下属数量查询失败");
        }
    };

    const confirmDelete = async () => {
        if (!detail?.id) return;
        setDeleting(true);
        try {
            await deletePort(Number(detail.id), PORT_WHARF_BERTH_TYPE.HARBOR.code);
            setConfirmMessage("");
            navigate(-1);
        } catch {
            setErrorMessage("港口删除失败");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section aria-label="port-management-detail-page" className="flex min-h-full flex-col">
            <Card>
                <ZhDetailHeader title="港口详情" />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <ZhMultiGrid>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            {/* 保留 Vue 详情网格中名称、简称各占两列的布局契约。 */}
                            <ZhBaseItem label="名称" span={2} value={display(detail.name)} />
                            <ZhBaseItem label="简称" span={2} value={display(detail.shortName)} />
                            <ZhBaseItem label="港口代码" value={display(detail.harborCode)} />
                            <ZhBaseItem label="面积" value={detail.area ? `${detail.area}㎡` : DEFAULT_VALUE} />
                            <ZhBaseItem label="联系人" value={display(detail.contactName)} />
                            <ZhBaseItem label="手机号" value={display(detail.phone)} />
                            {/* 位置类和简介字段沿用 Vue 的整行展示，避免详情内容被错误压缩。 */}
                            <ZhBaseItem label="位置" span={4} value={display(detail.fullAddress)} />
                            <ZhBaseItem label="经纬度" span={4} value={display(detail.latLon)} />
                            <ZhBaseItem label="简介" span={4} value={display(detail.remark)} />
                        </ZhGrid>
                    </ZhMultiGrid>
                ) : null}
                <Space className="mt-6">
                    {detail && canDeal ? (
                        banButton ? (
                            <Button disabled type="link">
                                修改
                            </Button>
                        ) : (
                            <Link to={`/logistics/portWharfBerth/portEdit?id=${detail.id}`}>修改</Link>
                        )
                    ) : null}
                    {detail ? <BerthLocationModal berth={detail} disabled={banButton} /> : null}
                    {detail && canDelete ? (
                        <Button disabled={banButton} onClick={() => void prepareDelete()}>
                            删除
                        </Button>
                    ) : null}
                    <Button type="link" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <Modal
                title="提示"
                open={Boolean(confirmMessage)}
                okText="确认"
                cancelText="取消"
                confirmLoading={deleting}
                onCancel={() => setConfirmMessage("")}
                onOk={() => void confirmDelete()}
            >
                {confirmMessage}
            </Modal>
        </section>
    );
}
